import { useState, useMemo, useEffect } from 'react';
import { Button, Form, Input, message } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { $ } from '../lib/api';
import { useAuthStore } from '../store/auth';

/** 密码强度：最低 8 位、含数字、含字母、至少 1 个特殊字符 */
interface PwdRule {
  ok: boolean;
  label: string;
}
function evaluatePassword(pwd: string): { score: number; rules: PwdRule[] } {
  const rules: PwdRule[] = [
    { ok: pwd.length >= 8, label: '≥ 8 位' },
    { ok: /[A-Za-z]/.test(pwd), label: '含字母' },
    { ok: /\d/.test(pwd), label: '含数字' },
    { ok: /[^A-Za-z0-9]/.test(pwd), label: '含特殊字符' },
  ];
  const score = rules.filter((r) => r.ok).length;
  return { score, rules };
}

export function ChangePasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const mustChange = useAuthStore((s) => s.mustChangePassword);
  const setSession = useAuthStore((s) => s.setSession);
  const accessToken = useAuthStore((s) => s.accessToken);
  const refreshToken = useAuthStore((s) => s.refreshToken);
  const logout = useAuthStore((s) => s.logout);

  const [submitting, setSubmitting] = useState(false);
  const [newPwd, setNewPwd] = useState('');
  const evaluation = useMemo(() => evaluatePassword(newPwd), [newPwd]);
  const allRulesPass = evaluation.rules.every((r) => r.ok);

  // 阻止用户用浏览器后退按钮绕过强制改密
  useEffect(() => {
    if (!mustChange) return;
    const onPopState = () => {
      // 拒绝后退 — 立即重新 push 自己
      window.history.pushState(null, '', location.pathname);
    };
    window.history.pushState(null, '', location.pathname);
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [mustChange, location.pathname]);

  // 未登录直接踢回登录页
  useEffect(() => {
    if (!accessToken) navigate('/login', { replace: true });
  }, [accessToken, navigate]);

  const onFinish = async (v: { oldPassword: string; newPassword: string; confirm: string }) => {
    if (v.newPassword !== v.confirm) {
      message.error('两次新密码不一致');
      return;
    }
    if (!allRulesPass) {
      message.error('新密码不符合复杂度要求');
      return;
    }
    if (v.newPassword === v.oldPassword) {
      message.error('新密码不能与旧密码相同');
      return;
    }
    setSubmitting(true);
    try {
      const res: any = await $.post('/auth/change-password', {
        oldPassword: v.oldPassword,
        newPassword: v.newPassword,
      });
      // 改密踢人:后端撤销所有旧会话并给本会话签发【新 token】(带新 pv)。用新 token 续期,否则写回旧 token 会被 pv 守卫秒踢回登录页。
      if (res?.accessToken && res?.refreshToken) {
        setSession({ accessToken: res.accessToken, refreshToken: res.refreshToken, user: res.user ?? user, mustChangePassword: false });
      } else if (user && accessToken && refreshToken) {
        setSession({ accessToken, refreshToken, user, mustChangePassword: false }); // 兜底:后端未返新 token 时退回旧行为
      }
      message.success('密码已更新，欢迎使用 HuanKe-CRM');
      // 跳回 dashboard（或来源页 — 现在简化只跳 dashboard）
      navigate('/dashboard', { replace: true });
    } finally {
      setSubmitting(false);
    }
  };

  const onLogoutClick = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const strengthColor = ['#ff4d4f', '#ff7a45', '#ffa940', '#52c41a', '#52c41a'][evaluation.score] || '#d9d9d9';
  const strengthLabel = ['很弱', '弱', '一般', '强', '很强'][evaluation.score] || '—';

  return (
    <div className="login-page">
      <div className="login-card" style={{ width: 420 }}>
        <div className="login-logo">
          <div className="login-logo-icon">幻</div>
          <div>
            <div className="login-title">修改登录密码</div>
            <div className="login-sub">
              {mustChange ? '首次登录或密码被重置 · 请立即修改密码' : '更新您的登录密码'}
            </div>
          </div>
        </div>

        {user && (
          <div style={{ background: 'var(--bg)', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 12 }}>
            当前账号：<strong>{user.realName}</strong> · {user.username}
          </div>
        )}

        <Form layout="vertical" onFinish={onFinish}>
          <Form.Item name="oldPassword" label="当前密码（初始密码或上次密码）" rules={[{ required: true, min: 6 }]}>
            <Input.Password placeholder="管理员发给您的初始密码" autoFocus />
          </Form.Item>

          <Form.Item name="newPassword" label="新密码" rules={[{ required: true }]}>
            <Input.Password
              placeholder="≥ 8 位 · 含字母 · 含数字 · 含特殊字符"
              onChange={(e) => setNewPwd(e.target.value)}
            />
          </Form.Item>

          {newPwd && (
            <div style={{ marginTop: -8, marginBottom: 12 }}>
              <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
                {[0, 1, 2, 3].map((i) => (
                  <div
                    key={i}
                    style={{
                      flex: 1,
                      height: 4,
                      borderRadius: 2,
                      background: i < evaluation.score ? strengthColor : '#f0f0f0',
                      transition: 'background 0.2s',
                    }}
                  />
                ))}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-3)', display: 'flex', justifyContent: 'space-between' }}>
                <span>强度：<span style={{ color: strengthColor, fontWeight: 500 }}>{strengthLabel}</span></span>
                <span style={{ display: 'flex', gap: 8 }}>
                  {evaluation.rules.map((r) => (
                    <span key={r.label} style={{ color: r.ok ? 'var(--success-text)' : 'var(--text-3)' }}>
                      {r.ok ? '✓' : '○'} {r.label}
                    </span>
                  ))}
                </span>
              </div>
            </div>
          )}

          <Form.Item name="confirm" label="确认新密码" dependencies={['newPassword']} rules={[
            { required: true },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) return Promise.resolve();
                return Promise.reject(new Error('两次密码不一致'));
              },
            }),
          ]}>
            <Input.Password placeholder="再输入一次新密码" />
          </Form.Item>

          <Button
            type="primary"
            htmlType="submit"
            loading={submitting}
            block
            disabled={!allRulesPass}
          >
            提交修改
          </Button>

          {mustChange && (
            <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text-3)', textAlign: 'center' }}>
              改密前无法访问其他功能。如需取消，
              <a onClick={onLogoutClick} style={{ marginLeft: 4 }}>退出登录</a>
            </div>
          )}
        </Form>
      </div>
    </div>
  );
}
